import type { ClassValue } from 'clsx';

import {
  OrderTotals,
  OrderTotalsAmount,
  OrderTotalsLabel,
  OrderTotalsLabelGroup,
  OrderTotalsLine,
  OrderTotalsNote,
  OrderTotalsVariant,
} from '@vs-shop/commerce-ui/orderTotals';
import { clsxm } from '@vs-shop/utils';
import { useIntl } from 'react-intl';
import type { ReactNode } from 'react';

import { IntlMoney } from '../../intl/Intl';
import type { CurrencyT } from '../../../../config/currency';

type Props = {
  currency: CurrencyT;
  discount: number;
  totalWithTax: number;
  couponName: string;
  isProvisional?: boolean;
  freeShippingProgress?: ReactNode;
  className?: ClassValue;
};

/**
 * The drawer summary shows the coupon, item total, and free-shipping progress.
 * `discount` is the positive amount the promotion takes off. It renders with a
 * minus sign.
 * This component sets the gap between the totals and progress sections.
 */
export default function CartDrawerSummary({
  currency,
  discount,
  totalWithTax,
  couponName,
  isProvisional = false,
  freeShippingProgress,
  className,
}: Props) {
  const intl = useIntl();

  return (
    <div className={clsxm('flex flex-col gap-8', className)}>
      <OrderTotals className="gap-2">
        {discount !== 0 && (
          <OrderTotalsLine variant={OrderTotalsVariant.PANEL_MUTED}>
            <OrderTotalsLabel variant={OrderTotalsVariant.PANEL_MUTED}>
              {couponName}
            </OrderTotalsLabel>
            <OrderTotalsAmount variant={OrderTotalsVariant.PANEL_MUTED}>
              <IntlMoney value={{ amount: -discount, currency }} />
            </OrderTotalsAmount>
          </OrderTotalsLine>
        )}

        <OrderTotalsLine variant={OrderTotalsVariant.PANEL_LEAD}>
          <OrderTotalsLabelGroup>
            <OrderTotalsLabel variant={OrderTotalsVariant.PANEL_LEAD}>
              {isProvisional
                ? intl.formatMessage({
                    id: 'cartDrawer.order.provisionalItemsTotal',
                    defaultMessage: 'Předběžná cena položek',
                  })
                : intl.formatMessage({
                    id: 'cartDrawer.order.total',
                    defaultMessage: 'Celková cena',
                  })}
            </OrderTotalsLabel>
            {!isProvisional && (
              <OrderTotalsNote>
                {intl.formatMessage({
                  id: 'cartDrawer.order.includingVAT',
                  defaultMessage: 'včetně DPH',
                })}
              </OrderTotalsNote>
            )}
          </OrderTotalsLabelGroup>
          <OrderTotalsAmount variant={OrderTotalsVariant.PANEL_LEAD}>
            <IntlMoney value={{ amount: totalWithTax, currency }} />
          </OrderTotalsAmount>
        </OrderTotalsLine>
      </OrderTotals>

      {freeShippingProgress}
    </div>
  );
}
